/**
 * 画面（Screen）と部品（Component）の入力スキーマ。欄の正は設計書 NE §1 の Screen と
 * Component の節（`nodes-edges.html#n1-screen` / `#n1-component`）である。
 *
 * 骨格（画面）と詳細（部品）に分かれる作りは今と同じである。画面の `elements[]` が部品の
 * **存在と表示名**を決め、部品の入力がその中身を埋める。
 *
 * 画面のドメイン（配信するホスト）・属するステージ・見るアクターは欄に持たず、読むたびに導く
 * （`core/elements/screen-derivations.ts`）。欄に持つと、部品や業務を直したときに画面の側だけが
 * 古いまま残る。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 画面（Screen）と部品（Component）の zod 入力スキーマ。骨格が部品の存在と表示名・種類・束ね・Figma の参照を、詳細が種別・操作する業務・表示条件・遷移先・画面入出力項目・アクション・入力チェックを持つ
 * @graph-connects none
 */
import { z } from 'zod';
import { localizedText, nonEmptyString } from '../node-schemas.js';
import { isSequentialLocalIds, localElementId, optionalSpecElementId, specElementId, } from './ids.js';
import { TERM_REPRESENTATIONS } from './term.js';
/**
 * 画面の種類 5 種（NE §1 Screen）。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 画面の種類（ページ / ウィザードの段 / タブ / ダイアログ / コールバック）の allowlist
 * @graph-connects none
 */
export const SCREEN_KINDS = ['page', 'step', 'tab', 'dialog', 'callback'];
/**
 * 部品の種別 5 種（NE §1-attrs の Component）。
 *
 * 表示名の後ろ半分になる（お届け先情報 ＋ 入力欄）ので、列挙値をそのままラベルに使う。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 部品の種別（ボタン / 入力欄 / 一覧 / 表示 / 画像）の allowlist
 * @graph-connects none
 */
export const COMPONENT_KINDS = ['button', 'input', 'list', 'display', 'image'];
/**
 * 入力チェックの種別 2 種（NE §1 Component）。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 入力チェックの種別（単項目 / 相関）の allowlist
 * @graph-connects none
 */
export const COMPONENT_VALIDATION_KINDS = ['single', 'correlated'];
/**
 * アクションから来た遷移の辺の条件 2 種（NE §2 `TRANSITIONS_TO_SCREEN`）。`normal` はアクションの
 * `transitionTo`、`error` は `onErrorTransitionTo` から来た辺である。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 遷移の辺の条件（正常 / 異常）の allowlist
 * @graph-connects none
 */
export const SCREEN_TRANSITION_CONDITIONS = ['normal', 'error'];
/**
 * 画面の骨格が並べる部品 1 件。存在と表示名の材料（クラスかプロパティの用語）を決める。
 *
 * `id` を省けば新規で、サーバーが採番する。値を渡せば、その部品の表示名を書き替える。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 画面の骨格が並べる部品 1 件（部品 id と名前の材料）の入力スキーマ
 * @graph-connects none
 */
export const screenComponentRefSchema = z.strictObject({
    id: optionalSpecElementId(),
    subject: specElementId(),
});
/**
 * Figma のフレームへの参照（NE §1 Screen の `figma`）。
 *
 * どちらも Figma の側の文字列で、仕様グラフが振る番号ではないので number にしない。レイアウトの
 * 正は Figma で、仕様グラフは行き先を組むための参照だけを持つ。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 画面が指す Figma のフレーム（ファイルの鍵とノード ID）の入力スキーマ
 * @graph-connects none
 */
export const screenFigmaSchema = z.strictObject({
    fileKey: nonEmptyString(),
    nodeId: nonEmptyString(),
});
/**
 * 画面（Screen）。
 *
 * ラベルは「用語（クラスかプロパティ） ＋ 動詞 ＋『画面』」で組む（ブランド請求書送付先入力画面）。`pathPattern`
 * は `/` 始まりの文字列で、仕様グラフが振る ID ではないので number にしない。
 *
 * `bundle` は設計書が仮の形で置いた欄である（束ねを欄で持つかは未決。NE §7 の U14）。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 画面（Screen）の zod 入力スキーマ。名前の作り・パスの形・部品の並び・種類・束ね・Figma の参照
 * @graph-connects none
 */
export const screenSkeletonSchema = z.strictObject({
    id: optionalSpecElementId(),
    subject: specElementId(),
    verb: specElementId(),
    pathPattern: nonEmptyString().regex(/^\//u, 'pathPattern は / で始まる'),
    elements: z.array(screenComponentRefSchema).min(1),
    kind: z.enum(SCREEN_KINDS).optional(),
    bundle: localizedText().optional(),
    figma: screenFigmaSchema.optional(),
});
/**
 * 用語への参照か数値そのもの。画面入出力項目の初期値が使う。
 *
 * 用語の番号と数値はどちらも number なので、裸の数では区別できない。判定表の範囲のセルと同じく
 * 鍵（`term` / `literal`）で分ける。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 画面入出力項目の初期値（値の用語への参照 / 数値）の入力スキーマ
 * @graph-connects none
 */
export const componentIoInitialValueSchema = z.union([
    z.strictObject({ term: specElementId() }),
    z.strictObject({ literal: z.number() }),
]);
/**
 * 画面入出力項目（NE §1 Component の `io`）。部品 1 つにつき 1 組。
 *
 * 型は用語（プロパティ）の番号か表現の型、初期値は値の用語か数値で受ける。形は設計書が仮で
 * 置いたもの（NE §7）で、どの欄も書かなくてよい。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 部品の画面入出力項目（型・桁・必須・初期値）の入力スキーマ
 * @graph-connects none
 */
export const componentIoSchema = z.strictObject({
    dataType: z.union([specElementId(), z.enum(TERM_REPRESENTATIONS)]).optional(),
    length: z.number().int().positive().optional(),
    required: z.boolean().optional(),
    initialValue: componentIoInitialValueSchema.optional(),
});
/**
 * 部品のアクション 1 つ（NE §1 Component の `actions[]`）。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 部品のアクション 1 つ（部品の中の番号・きっかけ・遷移先・異常時の遷移先・メッセージ）の入力スキーマ
 * @graph-connects none
 */
export const componentActionSchema = z.strictObject({
    id: localElementId(),
    trigger: localizedText(),
    transitionTo: specElementId().optional(),
    onErrorTransitionTo: specElementId().optional(),
    message: localizedText().optional(),
});
/**
 * 部品の入力チェック 1 つ（NE §1 Component の `validations[]`）。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 部品の入力チェック 1 つ（部品の中の番号・規則・種別・メッセージ）の入力スキーマ
 * @graph-connects none
 */
export const componentValidationSchema = z.strictObject({
    id: localElementId(),
    rule: localizedText(),
    kind: z.enum(COMPONENT_VALIDATION_KINDS).optional(),
    message: localizedText().optional(),
});
/** 部品の中の番号が 1 から欠番なしで振られているか。 @graph-connects isSequentialLocalIds [calls] 局所 ID が 1 起点の欠番なし連番かの判定 */
function hasSequentialIds(entries) {
    return isSequentialLocalIds(entries.map((entry) => entry.id));
}
/**
 * 部品（Component）。
 *
 * `id` は必須である。部品は画面の骨格が立てるので、詳細の入力は必ず既にある部品を指す
 * （今の `componentId` が担っていた役目を number が引き取る）。`screen` は親の画面の id で、
 * 親子は ID の綴りではなく欄で持つ。
 *
 * アクションと入力チェックの番号は部品の中で 1 から欠番なしで振る。欠番や重複を通すと、案件の
 * 差分で行を対応づける鍵と、遷移の辺のイベント（アクションの番号）が壊れる。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 部品（Component）の zod 入力スキーマ。種別・操作する業務・表示条件・遷移先・画面入出力項目・アクション・入力チェック
 * @graph-connects hasSequentialIds [calls] 部品の中の番号が 1 から欠番なしか
 */
export const componentElementSchema = z.strictObject({
    id: specElementId(),
    screen: specElementId(),
    kind: z.enum(COMPONENT_KINDS),
    operates: specElementId().optional(),
    visibleWhen: specElementId().optional(),
    transitionTo: z.array(specElementId()).optional(),
    io: componentIoSchema.optional(),
    actions: z
        .array(componentActionSchema)
        .refine(hasSequentialIds, 'actions[].id は部品の中で 1 から欠番なしで振る')
        .optional(),
    validations: z
        .array(componentValidationSchema)
        .refine(hasSequentialIds, 'validations[].id は部品の中で 1 から欠番なしで振る')
        .optional(),
});
//# sourceMappingURL=screen.js.map